import {clsx} from 'clsx'
import {createContext, type HTMLAttributes, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'

import {useChatStateLens} from '../utils/CopilotChatContext'
import styles from './ChatScrollContainer.module.css'

interface ChatScrollContext {
  isScrolledUp: boolean
  scrollToBottom: () => void
}

const ChatScrollContext = createContext<ChatScrollContext | null>(null)

/**
 * Functional container that handles auto-scroll on load and when new messages arrive. Provides context for
 * `useChatScroll`.
 */
export function ChatScrollContainer({children, ...props}: HTMLAttributes<HTMLDivElement>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)

  // Observe if scrolled to end
  // A stateful value is necessary for causing re-renders to show/hide the 'scroll to bottom' button, while a ref
  // allows us to synchronously track the value so the MutationObserver knows immediately
  const [isScrolledUp, setIsScrolledUp] = useState(false)
  const isScrolledUpRef = useRef(false) // useTrackingRef won't work here because it's not synchronous - it updates in an effect
  useEffect(() => {
    const scrollObserver = new IntersectionObserver(
      ([entry]) => {
        const newIsScrolledUp = !entry?.isIntersecting
        isScrolledUpRef.current = newIsScrolledUp
        setIsScrolledUp(newIsScrolledUp)
      },
      {
        root: containerRef.current!,
        threshold: 0,
        // The margin means the user must be scrolled up more than 50px to be considered 'scrolled up'. This helps
        // prevent edge cases where content scrolls in too fast and overtakes the logic, and provides a better UX because
        // it ensures the scrolling up is actually intentional
        rootMargin: '50px',
      },
    )

    scrollObserver.observe(endRef.current!)
    return () => scrollObserver.disconnect()
  }, [])

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => endRef.current?.scrollIntoView(false))
  }, [])

  // Scroll to end on mount and when the user sends a message
  const lastUserMessageId = useChatStateLens(s => s.messages.findLast(m => m.role === 'user')?.id)
  useEffect(scrollToBottom, [lastUserMessageId, scrollToBottom])

  // Scroll to end on change (as content streams), if not scrolled up
  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!isScrolledUpRef.current) scrollToBottom()
    })

    observer.observe(containerRef.current!, {childList: true, subtree: true})
    return () => observer.disconnect()
  }, [isScrolledUpRef, scrollToBottom])

  const contextValue = useMemo(() => ({isScrolledUp, scrollToBottom}), [isScrolledUp, scrollToBottom])

  return (
    <ChatScrollContext.Provider value={contextValue}>
      <div ref={containerRef} {...props} className={clsx(styles.container, props.className)}>
        {children}

        <div ref={endRef} style={{height: '1px', marginTop: '-1px'}} />
      </div>
    </ChatScrollContext.Provider>
  )
}

/** Get scroll state & controls for the chat scroll container. If used outside of a container, will be a noop. */
export const useChatScroll = () => {
  const context = useContext(ChatScrollContext)
  if (!context) throw new Error('useChatScroll may only be called in a descendant of ChatScrollContainer')
  return context
}

try{ ChatScrollContext.displayName ||= 'ChatScrollContext' } catch {}
try{ ChatScrollContainer.displayName ||= 'ChatScrollContainer' } catch {}